import { useCallback, useEffect, useRef, useState } from "react";

export const useShare = (shareText: string, shareUrl?: string) => {
  const shareTextWithLink = `${shareText}\n${shareUrl ?? ""}`;
  const shareButtonsElementRef = useRef<HTMLDivElement>(null);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (!showToast) {
      return;
    }
    const timer = setTimeout(() => {
      setShowToast(false);
    }, 2000);
    return (): void => clearTimeout(timer);
  }, [showToast]);

  const handleKeyDown = useCallback((ev: KeyboardEvent): void => {
    if (ev.code === "Escape") {
      setIsShareOpen(false);
    }
  }, []);

  const handleClick = useCallback((ev: MouseEvent): void => {
    if (!shareButtonsElementRef.current || !(ev.target instanceof Node)) {
      return;
    }

    if (!shareButtonsElementRef.current.contains(ev.target)) {
      setIsShareOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isShareOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("click", handleClick);
      return;
    }

    document.removeEventListener("keydown", handleKeyDown);
    document.removeEventListener("click", handleClick);
  }, [handleClick, handleKeyDown, isShareOpen]);

  const copy = async () => {
    await navigator.clipboard.writeText(shareTextWithLink);
    setShowToast(true);
  };

  const handleShareClick = async (): Promise<void> => {
    try {
      if (!navigator.share) {
        // シェア機能がサポートされてない場合は何もしない
        return;
      }

      await navigator.share({
        title: shareText,
        text: shareText,
        url: shareUrl,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const toggleShareOpen = () => {
    setIsShareOpen((prevState) => !prevState);
  };

  return {
    copy,
    handleShareClick,
    isShareOpen,
    toggleShareOpen,
    shareButtonsElementRef,
    shareTextWithLink,
    showToast,
  };
};
